/* Markup for the companion-product offer: the list of add-on cards shown after
   the extras step, the pickers each card expands into, and the details popup.

   Pure rendering — every value comes from a view model built by
   personalizer-crosssell.js, and money formatting is passed in. Keeping the
   HTML here means the list can grow from one companion product to several
   without the state module changing at all. */
(function () {
  'use strict';

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  /* Price line: what they pay, then what it lists at. The percent chip is for
     the popup only — on a card it competes with the strike-through for the
     same job and pushes the Add button onto its own line. */
  function priceHtml(v, fmt, withCut) {
    return '<p class="pzx-price">'
      + (v.prefix ? '<span class="pzx-price__from">' + esc(v.prefix) + '</span>' : '')
      + '<b>' + esc(fmt(v.nowCents)) + '</b>'
      + (v.nowCents < v.fullCents ? '<s>' + esc(fmt(v.fullCents)) + '</s>' : '')
      + (withCut && v.discountPct > 0 ? '<span class="pzx-price__cut">−' + v.discountPct + '%</span>' : '')
      + '</p>';
  }

  /* withWas: the popup is where people compare, so each chip carries the
     struck original next to the discounted price. On the card the strike would
     just repeat what the price line above it already says. */
  function sizesHtml(v, fmt, withWas) {
    if (v.variants.length < 2) return '';
    return '<div class="pzx-field">'
      + '<p class="pzx-field__label">' + esc(v.optionLabel) + '</p>'
      + '<div class="pzx-sizes">'
      + v.variants.map(function (o) {
        return '<button type="button" class="pzx-size' + (o.id === v.variantId ? ' is-on' : '') + '"'
          + ' data-pzx-item="' + esc(v.id) + '" data-pzx-size="' + o.id + '"'
          + ' aria-pressed="' + (o.id === v.variantId ? 'true' : 'false') + '">'
          + '<span>' + esc(o.title) + '</span>'
          // one price row: bold price, then the struck compare-at and the cut —
          // every chip stays two lines tall whether or not it carries a cut
          + '<span class="pzx-size__row">'
          + '<small>' + esc(fmt(o.nowCents)) + '</small>'
          + (o.compare > o.price ? '<s>' + esc(fmt(o.compare)) + '</s>' : (withWas ? '<s>' + esc(fmt(o.price)) + '</s>' : ''))
          + (o.compare > o.price ? '<em class="pzx-size__cut">−' + Math.round((1 - o.price / o.compare) * 100) + '%</em>' : '')
          + '</span></button>';
      }).join('')
      + '</div>'
      + (v.sizeHint ? '<p class="pzx-field__hint pzx-sizes__hint">' + esc(v.sizeHint) + '</p>' : '')
      + '</div>';
  }

  /* Where the photos come from. Reusing the order's is the quiet default; the
     alternative exists because plenty of people want the hoop to be a
     different face from the mobile. */
  function photosHtml(v, maxMb) {
    if (!v.usePhotos) return '';
    var opt = function (mode, label, sub) {
      return '<button type="button" class="pzx-photoopt' + (v.photoMode === mode ? ' is-on' : '') + '"'
        + ' data-pzx-item="' + esc(v.id) + '" data-pzx-photomode="' + mode + '"'
        + ' aria-pressed="' + (v.photoMode === mode ? 'true' : 'false') + '">'
        + '<b>' + esc(label) + '</b><span>' + esc(sub) + '</span></button>';
    };
    return '<div class="pzx-field">'
      + '<p class="pzx-field__label">Photos</p>'
      + (v.photoOnlyNew ? '' : '<div class="pzx-photoopts">'
        + opt('reuse', 'Use my order photos', 'the ones you just uploaded')
        + opt('new', 'Upload different photos', 'a different face, or a better shot')
        + '</div>')
      // filled by syncPick(): rebuilding it here on every repaint would take
      // the uploader below it down with it
      + '<div class="pzx-pick" data-pzx-pickwrap="' + esc(v.id) + '"></div>'
      + '<div class="pzx-photos" data-pzx-photos="' + esc(v.id) + '"'
      + (v.photoMode === 'new' ? '' : ' hidden') + '>'
      + uploaderHtml(maxMb)
      + '</div>'
      + laterHtml(v)
      + '</div>';
  }

  /* "I'll send the photo later" — for the person who has not picked a shot
     yet; the tick waives the upload and the line says so. */
  function laterHtml(v) {
    if (!v.photoLater) return '';
    return '<label class="pzx-later">'
      + '<input type="checkbox" data-pzx-item="' + esc(v.id) + '" data-pzx-later' + (v.later ? ' checked' : '') + '>'
      + '<span><b>' + esc(v.photoLater.label || "I'll send the photo later") + '</b>'
      + (v.photoLater.sub ? '<small>' + esc(v.photoLater.sub) + '</small>' : '') + '</span>'
      + '</label>';
  }

  /* Shown only when the piece holds fewer portraits than the order has photos:
     the customer says which face this one is for, instead of the studio
     receiving two photos for one portrait and having to ask. */
  function photoPickHtml(v) {
    if (!v.photoPick) return '';
    var all = v.photoPick.chosen.length === v.photoPick.photos.length;
    return ''
      + '<p class="pzx-pick__q">' + esc(v.photoPick.question)
      + (v.photoPick.photos.length > 1 && !all
        ? '<button type="button" class="pzx-pick__all" data-pzx-item="' + esc(v.id) + '" data-pzx-pickall>'
          + 'Use all ' + v.photoPick.photos.length + '</button>'
        : '')
      + '</p>'
      + (v.photoPick.hint ? '<p class="pzx-pick__hint">' + esc(v.photoPick.hint) + '</p>' : '')
      + '<div class="pzx-pick__grid">'
      + v.photoPick.photos.map(function (p, i) {
        var on = v.photoPick.chosen.indexOf(p.url) !== -1;
        return '<button type="button" class="pzx-pick__item' + (on ? ' is-on' : '') + '"'
          + ' data-pzx-item="' + esc(v.id) + '" data-pzx-photopick="' + esc(p.url) + '"'
          + ' aria-pressed="' + (on ? 'true' : 'false') + '"'
          + ' aria-label="Photo ' + (i + 1) + '">'
          + '<img src="' + esc(p.url) + '" alt="">'
          + '<i aria-hidden="true">\u2713</i></button>';
      }).join('')
      + '</div>';
  }

  /* Mirrors snippets/personalizer-step-photos.liquid: PersonalizerUpload looks
     for these hooks inside whatever element it is handed. */
  function uploaderHtml(maxMb) {
    return '<div class="personalizer__photos" data-photo-group>'
      + '<label class="personalizer__file-drop">'
      + '<input type="file" class="personalizer__file-input visually-hidden"'
      + ' accept="image/jpeg,image/png,image/webp" multiple data-photo-input'
      + ' aria-label="Add photos for this piece">'
      + '<span class="personalizer__file-drop-text">'
      + '<span class="personalizer__file-drop-icon" aria-hidden="true">📸</span>'
      + '<b>Tap to add photos</b>'
      + '<span class="personalizer__file-drop-sub">JPG · PNG · WEBP · up to '
      + (maxMb || 15) + ' MB each</span></span></label>'
      + '<p class="personalizer__notice" data-photo-notice hidden></p>'
      + '<ul class="personalizer__shots" data-photo-previews></ul>'
      + '<div class="personalizer__upload-progress" data-photo-progress hidden>'
      + '<div class="personalizer__upload-progress-fill" data-photo-progress-fill></div></div>'
      + '<p class="personalizer__error" data-photo-error role="alert" hidden></p>'
      + '</div>';
  }

  function fieldHtml(v, f, scope) {
    var val = v.values[f.key] || '';
    if (f.type === 'swatch') {
      return '<div class="pzx-field">'
        + '<p class="pzx-field__label">' + esc(f.label)
        + '<b>' + esc(val) + '</b></p>'
        + '<div class="pzx-swatches">'
        + (f.values || []).map(function (o) {
          var on = val === o.value;
          return '<button type="button" class="pzx-swatch' + (on ? ' is-on' : '') + '"'
            + ' data-pzx-item="' + esc(v.id) + '" data-pzx-field="' + esc(f.key) + '"'
            + ' data-pzx-val="' + esc(o.value) + '" aria-pressed="' + (on ? 'true' : 'false') + '"'
            + ' title="' + esc(o.value) + '" aria-label="' + esc(o.value) + '">'
            + (o.image ? '<img src="' + esc(o.image) + '" alt="" loading="lazy">'
              : '<i style="background:' + esc(o.hex || '#eee') + '"></i>')
            + '</button>';
        }).join('')
        + '</div></div>';
    }
    var id = 'pzx-' + scope + '-' + v.id + '-' + f.key;
    if (f.type === 'note') {
      return '<div class="pzx-field">'
        + '<p class="pzx-field__label"><label for="' + esc(id) + '">' + esc(f.label) + '</label>'
        + (f.required ? '' : '<em>optional</em>') + '</p>'
        + '<textarea id="' + esc(id) + '" class="pzx-input pzx-input--note" rows="3"'
        + ' data-pzx-item="' + esc(v.id) + '" data-pzx-field="' + esc(f.key) + '"'
        + ' maxlength="' + (f.max || 500) + '" placeholder="' + esc(f.placeholder || '') + '">' + esc(val) + '</textarea>'
        + (f.hint ? '<p class="pzx-field__hint">' + esc(f.hint) + '</p>' : '')
        + '</div>';
    }
    return '<div class="pzx-field">'
      + '<p class="pzx-field__label"><label for="' + esc(id) + '">' + esc(f.label) + '</label>'
      + (f.required ? '' : '<em>optional</em>') + '</p>'
      + '<input id="' + esc(id) + '" class="pzx-input" type="text"'
      + ' data-pzx-item="' + esc(v.id) + '" data-pzx-field="' + esc(f.key) + '"'
      + ' maxlength="' + (f.max || 40) + '" placeholder="' + esc(f.placeholder || '') + '"'
      + ' value="' + esc(val) + '">'
      + (f.hint ? '<p class="pzx-field__hint">' + esc(f.hint) + '</p>' : '')
      + '</div>';
  }

  function pickers(v, scope, fmt, maxMb) {
    return sizesHtml(v, fmt, scope === 'modal')
      + (v.fields || []).map(function (f) { return fieldHtml(v, f, scope); }).join('')
      // the uploader is a card-only control: two of them on one page would
      // fight over the same files
      + (scope === 'card' ? photosHtml(v, maxMb) : '');
  }

  function addLabel(v) {
    return v.on ? '✓ Added to my order' : '+ Add to my order';
  }

  /* One add-on card. Collapsed it is a single glance: photo, what it is, what
     it costs. The pickers only appear once they have said yes. */
  function card(v, fmt) {
    return '<article class="pzx-card' + (v.on ? ' is-on' : '') + '" data-pzx-card="' + esc(v.id) + '">'
      // the whole row toggles, like the Extras rows a step earlier; the photo
      // and the details link inside it still open the popup
      + '<div class="pzx-card__main" role="button" tabindex="0"'
      + ' data-pzx-item="' + esc(v.id) + '" data-pzx-toggle'
      + ' aria-pressed="' + (v.on ? 'true' : 'false') + '">'
      + '<span class="pzx-card__media" role="button" tabindex="0"'
      + ' data-pzx-item="' + esc(v.id) + '" data-pzx-open'
      + ' aria-label="See photos and details">'
      + (v.image ? '<img src="' + esc(v.image) + '" alt="' + esc(v.title) + '">' : '')
      + '</span>'
      + '<div class="pzx-card__body">'
      + '<h4 class="pzx-card__name">' + esc(v.title) + '</h4>'
      + (v.rowText ? '<p class="pzx-card__hook">' + esc(v.rowText) + '</p>' : '')
      + (v.photoNote ? '<p class="pzx-card__reuse">' + esc(v.photoNote) + '</p>' : '')
      + '<button type="button" class="pzx-details" data-pzx-item="' + esc(v.id) + '" data-pzx-open>'
      + esc(v.detailsLabel) + ' <span aria-hidden="true">→</span></button>'
      + priceHtml(v, fmt, true)
      + '</div>'
      + '<span class="pzx-card__check" aria-hidden="true">✓</span>'
      + '</div>'
      + '<div class="pzx-card__choices" data-pzx-choices="' + esc(v.id) + '"' + (v.on ? '' : ' hidden') + '></div>'
      + '<p class="pzx-err" data-pzx-err="' + esc(v.id) + '" role="alert" hidden></p>'
      + '</article>';
  }

  function list(head, views, fmt) {
    return '<section class="pzx">'
      + '<header class="pzx__head">'
      + '<h3 class="pzx__title">' + esc(head.heading) + '</h3>'
      + (head.sub ? '<p class="pzx__sub">' + esc(head.sub) + '</p>' : '')
      + '</header>'
      // No decline button: the sticky Add to cart below is already the way past
      // this screen, and a second "no" only invites one.
      + '<div class="pzx__cards">' + views.map(function (v) { return card(v, fmt); }).join('') + '</div>'
      + '</section>';
  }

  function modal(v, fmt) {
    var imgs = v.images || [];
    return '<div class="pzx-modal__inner">'
      + '<div class="pzx-modal__bar">'
      + '<button type="button" class="pzx-modal__x" data-pzx-close aria-label="Close">✕</button>'
      + '</div>'
      + '<div class="pzx-modal__grid">'
      + '<div class="pzx-modal__gallery">'
      + '<div class="pzx-modal__slides" data-pzx-slides>'
      + imgs.map(function (u, i) {
        return '<img class="pzx-modal__slide" data-pzx-slide="' + i + '" src="' + esc(u) + '"'
          + ' alt="' + esc(v.title) + ' photo ' + (i + 1) + '"'
          + (i === 0 ? '' : ' loading="lazy"') + '>';
      }).join('')
      + '</div>'
      + (imgs.length > 1
        ? '<button type="button" class="pzx-modal__nav pzx-modal__nav--prev" data-pzx-slidenav="-1" aria-label="Previous photo">\u2039</button>'
          + '<button type="button" class="pzx-modal__nav pzx-modal__nav--next" data-pzx-slidenav="1" aria-label="Next photo">\u203a</button>'
          + '<div class="pzx-modal__thumbs">' + imgs.map(function (u, i) {
            return '<button type="button" class="pzx-modal__thumb' + (i === 0 ? ' is-on' : '')
              + '" data-pzx-thumb="' + i + '"><img src="' + esc(u) + '" alt="" loading="lazy"></button>';
          }).join('') + '</div>'
        : '')
      + '</div>'
      + '<div class="pzx-modal__body">'
      + '<h3 class="pzx-modal__title">' + esc(v.title) + '</h3>'
      + priceHtml(v, fmt, true)
      + ((v.usps || []).length ? '<ul class="pzx-usps">' + v.usps.map(function (u) {
        // "claim — qualifier": the claim carries the weight, so it gets the
        // weight. Lines without a dash are bold throughout.
        var parts = String(u).split(' \u2014 ');
        return '<li><b>' + esc(parts[0]) + '</b>'
          + (parts.length > 1 ? '<span> \u2014 ' + esc(parts.slice(1).join(' \u2014 ')) + '</span>' : '')
          + '</li>';
      }).join('') + '</ul>' : '')
      + (v.summary ? '<p class="pzx-modal__desc">' + esc(v.summary) + '</p>' : '')
      + '<div class="pzx-card__choices pzx-card__choices--modal" data-pzx-choices-modal="' + esc(v.id) + '">'
      + pickers(v, 'modal', fmt, 0) + '</div>'
      + '<p class="pzx-err" data-pzx-err-modal role="alert" hidden></p>'
      + '<button type="button" class="pzx-add pzx-add--modal' + (v.on ? ' is-on' : '') + '" data-pzx-item="' + esc(v.id) + '" data-pzx-modal-add>'
      + (v.on ? '✓ Added — back to my order' : 'Add to my order — ' + fmt(v.nowCents)) + '</button>'
      + '<a class="pzx-modal__link" href="' + esc(v.url) + '" target="_blank" rel="noopener">'
      + 'Open the full product page ↗</a>'
      + '</div></div></div>';
  }

  window.PersonalizerCrossRender = {
    list: list,
    photoPick: photoPickHtml,
    addLabel: addLabel,
    pickers: pickers,
    modal: modal,
    priceHtml: priceHtml,
  };
})();
